/* eslint-disable no-restricted-imports */
import React, { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import {
    Link,
    NavLink,
    NavLinkProps,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';
import { useIsTabbarHidden } from '../../hooks/useIsTabbarHidden';
import { useOpenDialog } from '../../routing/ModalRoute';
import { DIALOGS } from '../../routing/constants';
import { isDeclinedAdmin } from '../../utils/accessService';
import { useIsMobile } from '../../utils/getIsMobile';
import { down, up } from '../../utils/styles/breakpoints';
import { px } from '../../utils/styles/px';
import { useIsAdvertisingCatalogEnabled } from '../../utils/useIsAdvertisingCatalogEnabled';
import { useTranslation } from '../../utils/useTranslation';
import { AdvertisingMenuButton } from '../advertising/AdvertisingMenuButton/AdvertisingMenuButton';
import { IconAppQr } from '../icon/IconAppQr';
import { IconPlus } from '../icon/IconPlus';
import { Button } from '../input/Button';
import { Box } from '../layout/Box';
import { DataTestId } from '../layout/DataTestIdWrapper';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { CpaPromoBalance } from '../users/CpaPromoBalance/CpaPromoBalance';
import { UserUI } from '../users/types';
import { HEADER_HEIGHT } from './DashboardHeader';
import { Logo, LogoCompact } from './Logo';
import { CONTENT_WIDTH } from './constants';
import { usePaddingBottom } from './hooks/usePaddingBottom';
import { MobileTabbar } from './mobileTabbar/MobileTabbar';

const TABLET_MENU_WIDTH = 88;

export const DESKTOP_MENU_WIDTH = 316;

interface AsideParams {
    isMobile?: boolean | null;
}

const Aside = styled(Box)
    .withConfig({ displayName: 'Aside' })
    .attrs<AsideParams>((props) => ({
        px: 1,
        width: {
            xs: '100%',
            sm: TABLET_MENU_WIDTH,
            lg: props.isMobile ? TABLET_MENU_WIDTH : DESKTOP_MENU_WIDTH,
        },
        zIndex: { xs: 1000, md: 'auto' },
        paddingTop: props.isMobile ? { sm: HEADER_HEIGHT / 8 } : undefined,
    }))`
    box-sizing: border-box;
    position: fixed;
    bottom: 0;
    left: 0;
    box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.12) inset;
    background: rgba(var(--background-primary-rgb), 0.65);
    -webkit-backdrop-filter: blur(var(--blur-radius-l));
    backdrop-filter: blur(var(--blur-radius-l));
    transform: translate3d(0, 0, 0);

    ${up('sm')} {
        top: 0;
        background: none;
        backdrop-filter: none;
        box-shadow: none;
    }
`;

const Footer = styled(Flex).withConfig({ displayName: 'Footer' })`
    position: fixed;
    left: 0;
    bottom: 0;
    max-width: calc((100% - ${CONTENT_WIDTH}px) / 2);
    ${down('lg')} {
        display: none;
    }

    @media (max-height: 660px) {
        display: none;
    }
`;

const DashboardMenuItemRoot = styled(Flex)
    .withConfig({ displayName: 'DashboardMenuItemRoot' })
    .attrs({
        spacing: 2,
        direction: 'row',
        alignItems: 'center',
        width: { xs: 'auto', sm: '100%' },
        p: { xs: 1, sm: 1.5 },
    })`
    text-decoration: none;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-delay: 0ms;
    transition-property: color, transform;
    color: var(--color-secondary);
    cursor: pointer;

    &:hover,
    &:focus,
    &:active,
    &.active {
        color: var(--color-primary);
    }
`;

interface DashboardMenuItemProps extends NavLinkProps {
    id: string;
    variant?: 'flat' | 'button';
    selectedIcon: ReactElement;
    unselectedIcon: ReactElement;
    title: string;
    subtitle?: string;
    cpaBalance?: number;
    linkMatchPaths?: string[];
}

function DashboardMenuItem(props: DashboardMenuItemProps) {
    const {
        selectedIcon,
        unselectedIcon,
        title,
        subtitle,
        id,
        linkMatchPaths,
        cpaBalance,
        ...other
    } = props;
    const t = useTranslation();

    const { pathname } = useLocation();

    const subtitleElem = subtitle ? (
        <Typography variant="caption" color="inherit">
            {t(subtitle)}
        </Typography>
    ) : null;

    return (
        <DashboardMenuItemRoot as={NavLink} {...other}>
            {({ isActive: isActiveBase }) => {
                const isActive = isActiveBase || linkMatchPaths?.includes(pathname);

                return (
                    <>
                        <Flex fontSize={24} position="relative">
                            {isActive ? selectedIcon : unselectedIcon}
                        </Flex>
                        <Box display={{ xs: 'none', lg: 'initial' }}>
                            <Typography variant="body" color="inherit" weight="bold">
                                {t(title)}
                            </Typography>
                            {cpaBalance ? (
                                <CpaPromoBalance balance={cpaBalance} mt={0.5} />
                            ) : (
                                subtitleElem
                            )}
                        </Box>
                    </>
                );
            }}
        </DashboardMenuItemRoot>
    );
}

const Content = styled(Box)
    .withConfig({ displayName: 'Content' })
    .attrs({
        display: 'flex',
        width: '100%',
        minHeight: '100%',
        pl: { sm: px(TABLET_MENU_WIDTH), lg: px(DESKTOP_MENU_WIDTH) },
        pr: { sm: px(TABLET_MENU_WIDTH), lg: 0 },
    })`
    --safe-area-inset-bottom: calc(56px + env(safe-area-inset-bottom, 0));

    ${up('sm')} {
        --safe-area-inset-bottom: env(safe-area-inset-bottom, 0);
    }
`;

const QrContainer = styled(Box).withConfig({ displayName: 'QrContainer' }).attrs({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    cursor: 'pointer',
    mt: 1.4,
    width: '180px',
    height: '180px',
})`
    color: var(--color-secondary);

    &:hover {
        color: var(--color-primary);
    }
    @media (max-height: 856px) {
        width: 100px;
        height: 100px;
    }

    @media (max-height: 796px) {
        display: none;
    }
`;

const StyledIconAppQr = styled(IconAppQr).withConfig({ displayName: 'StyledIconAppQr' })`
    width: 100%;
    height: 100%;
`;

const DistributionContainer = styled(Box)
    .withConfig({ displayName: 'DistributionContainer' })
    .attrs({
        width: '100%',
        borderTop: '1px solid var(--color-secondary)',
        mt: 0.5,
        pt: 1,
    })``;

const CreateButton = styled(Button)
    .withConfig({ displayName: 'CreateButton' })
    .attrs({
        color: 'primary',
        size: 'l',
        margin: { xs: '12px 0px', lg: '12px' },
        isHoverEnabled: false,
    })``;

interface DashboardLayoutProps {
    menuItems: DashboardMenuItemProps[];
    children: ReactNode;
    onAppDownloadClick: VoidFunction;
    user: UserUI | undefined;
    cpaBalance?: number;
}

export const MOBILE_FOOTER_HEIGHT = 56;

export const MOBILE_HEADER_HEIGHT = 52;

export function DashboardLayout(props: DashboardLayoutProps) {
    const { children, menuItems, onAppDownloadClick, user, cpaBalance } = props;
    const t = useTranslation();
    const { pathname } = useLocation();
    const [query, setQuery] = useSearchParams();
    const isMobile = useIsMobile();
    const shouldHideMobileTabbar = useIsTabbarHidden();
    const openDialog = useOpenDialog();
    const navigate = useNavigate();
    const isFromSearchApp = query.get('morda_platform') === 'search_app';

    const pb = usePaddingBottom();

    const openCreateCollectionDialog = useCallback(() => {
        openDialog(DIALOGS.CREATE_COLLECTION);
    }, [openDialog]);

    const needOpenCreateCollectionDialog = query.get('promo') === 'create_collection';

    useEffectOnce(() => {
        if (needOpenCreateCollectionDialog) {
            query.delete('promo');

            setQuery(query, { replace: true });
            openCreateCollectionDialog();
        }
    });

    useEffectOnce(() => {
        if (isFromSearchApp) {
            document.body.classList.add('primary_background');
        }

        return () => {
            document.body.classList.remove('primary_background');
        };
    });

    const advertisigCatalogEnabled = useIsAdvertisingCatalogEnabled();

    const tabbar = useMemo(() => {
        if (isMobile === null) {
            return null;
        }

        if (isMobile) {
            return (
                <MobileTabbar
                    user={user}
                    cpaBalance={cpaBalance}
                    openMultiProfileDialog={(data: { user: UserUI }) =>
                        openDialog(DIALOGS.OTHER_MENU_OPTIONS, data)
                    }
                />
            );
        }

        const createButtonEnabled = user?.profile && !isDeclinedAdmin(user?.role);

        return (
            <Flex
                p={1}
                width="100%"
                direction={{ xs: 'row', sm: 'column' }}
                height={{ xs: MOBILE_FOOTER_HEIGHT, sm: '100%' }}
                justifyContent={{ xs: 'space-between', sm: 'flex-start' }}
            >
                {menuItems.map((menuItem, index) => (
                    <DashboardMenuItem {...menuItem} key={index} role="menuitem" />
                ))}
                {advertisigCatalogEnabled ? <AdvertisingMenuButton target="_self" /> : null}
                {createButtonEnabled ? (
                    <CreateButton as={Link} to={'/posts/create'}>
                        <Box mr={{ xs: 'auto', lg: 1 }}>
                            <IconPlus size={16} />
                        </Box>
                        <Box display={{ xs: 'none', lg: 'block' }}>{t('добавить пост')}</Box>
                    </CreateButton>
                ) : (
                    <CreateButton
                        dataTestId={DataTestId.SIDEBAR_CREATE_COLLECTION}
                        onClick={openCreateCollectionDialog}
                    >
                        <Box mr={{ xs: 'auto', lg: 1 }}>
                            <IconPlus size={16} />
                        </Box>
                        <Box display={{ xs: 'none', lg: 'block' }}>{t('Создать коллекцию')}</Box>
                    </CreateButton>
                )}
            </Flex>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, user, openDialog, menuItems, advertisigCatalogEnabled]);

    const onLogoClick = useCallback(() => {
        if (pathname !== '/') {
            navigate('/');
        }
    }, [navigate, pathname]);

    const logo = useMemo(() => {
        const logoCompact = <LogoCompact height={56} onClick={onLogoClick} />;

        if (isMobile) {
            return (
                <Box pt={3} pb={1} px={1} display={{ xs: 'none', sm: 'block' }}>
                    {logoCompact}
                </Box>
            );
        }

        return (
            <>
                <Box pt={2.5} pb={0.5} px={2.5} display={{ xs: 'none', lg: 'block' }}>
                    <Logo height={64} onClick={onLogoClick} />
                </Box>
                <Box pt={3} pb={1} px={1} display={{ xs: 'none', sm: 'block', lg: 'none' }}>
                    {logoCompact}
                </Box>
            </>
        );
    }, [isMobile, onLogoClick]);

    return (
        <Box display="flex" width="100%" position="relative" height="100%" pb={pb}>
            <Content>{children}</Content>
            <Aside
                display={{ xs: shouldHideMobileTabbar ? 'none' : 'block', sm: 'block' }}
                isMobile={isMobile}
            >
                {logo}
                {tabbar}
            </Aside>

            <Footer p={3} spacing={0.75}>
                <Typography
                    as="a"
                    dataTestId={DataTestId.SIDEBAR_LINK_ABOUT}
                    href="https://yandex.ru/support2/rythm/ru/"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('О сервисе')}
                </Typography>
                <Typography
                    dataTestId={DataTestId.SIDEBAR_APP_LINK}
                    variant="caption"
                    weight="regular"
                    color="secondary"
                    cursor="pointer"
                    onClick={onAppDownloadClick}
                >
                    {t('Приложение Яндекс Ритм')}
                </Typography>
                <Typography
                    as="a"
                    dataTestId={DataTestId.SIDEBAR_LINK_FOR_BUSINESS}
                    href="https://yandex.ru/rythm/forbusiness"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('Для бизнесов')}
                </Typography>
                <Typography
                    as="a"
                    dataTestId={DataTestId.SIDEBAR_LINK_FOR_USERS}
                    href="https://yandex.ru/rythm/welcome"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('Для пользователей')}
                </Typography>
                <Typography
                    as="a"
                    dataTestId={DataTestId.SIDEBAR_LINK_TERMS}
                    href="https://yandex.ru/legal/ritm_termsofuse"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('Пользовательское соглашение')}
                </Typography>
                <Typography
                    as="a"
                    dataTestId={DataTestId.SIDEBAR_LINK_CONFIDENTIAL}
                    href="https://yandex.ru/legal/confidential"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('Политика конфиденциальности')}
                </Typography>
                <Typography
                    as="a"
                    dataTestId={DataTestId.SIDEBAR_LINK_RECOMMENDATIONS}
                    href="https://yandex.ru/legal/recommendations"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('Правила рекомендательных технологий')}
                </Typography>
                <DistributionContainer>
                    <Typography
                        dataTestId={DataTestId.SIDEBAR_APP_DOWNLOAD}
                        cursor="pointer"
                        variant="caption"
                        weight="regular"
                        color="secondary"
                        onClick={() => openDialog(DIALOGS.APPS_QR)}
                    >
                        {t('Скачайте приложение для смартфонов')}
                    </Typography>
                    <QrContainer
                        dataTestId={DataTestId.SIDEBAR_APP_QR}
                        onClick={() => openDialog(DIALOGS.APPS_QR)}
                    >
                        <StyledIconAppQr fontSize={180} />
                    </QrContainer>
                </DistributionContainer>
            </Footer>
        </Box>
    );
}
